<template>
  <div>
    <Banner type="part" />
    <div class="page-container j-container-box-shadow container">
      <span class="j-title title">{{ $t('part.title') }}</span>

      <a-divider style="margin: 30px 0;" />

      <a-row :gutter="[0, { xs: 24, sm: 24 }]" v-if="vehicles.length > 0">
        <a-col :xs="24" :sm="24" :md="8">
          <a-form
            class="part-no-form"
            layout="inline"
            ref="modelForm"
            :model="partNoQueryParam"
          >
            <a-form-item
              name="modelNum"
              :rules="{
                type: 'string',
                required: true,
                message: $t('part.search_by_part_no_msg')
              }"
            >
              <a-input-search
                v-model:value="partNoQueryParam.modelNum"
                :placeholder="$t('part.search_by_part_no')"
                @search="onSearchNo"
                @change="onSearchModelNumChange"
              >
                <template #enterButton>
                  <a-button :style="getSearchBtn1Style()">
                    <template #icon>
                      <SearchIcon :isActive="!!partNoQueryParam.modelNum" />
                    </template>
                  </a-button>
                </template>
              </a-input-search>
            </a-form-item>
          </a-form>
        </a-col>
        <a-col :xs="0" :sm="0" :md="2" style="text-align: center;">
          <a-divider type="vertical" style="height: 32px;" />
        </a-col>
        <a-col :xs="24" :sm="24" :md="14">
          <a-form
            ref="numForm"
            :model="partNameQueryParam"
            class="part-name-form"
            layout="inline"
          >
            <a-form-item
              name="vehicleId"
              :rules="{
                type: 'number',
                required: true,
                message: $t('part.select_vehicle_msg')
              }"
            >
              <a-select
                allow-clear
                show-search
                :filter-option="false"
                @search="fetchVehicle"
                v-model:value="partNameQueryParam.vehicleId"
                :placeholder="$t('part.select_vehicle')"
                @change="handleVehicleChange"
              >
                <a-select-option
                  v-for="v in vehicleDropdownList"
                  :key="v.id"
                  :value="v.id"
                >
                  {{ $t(v.vehicleName) }}
                </a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item
              name="name"
              :rules="{
                type: 'string',
                required: true,
                message: $t('part.search_by_part_name_msg')
              }"
            >
              <a-input-search
                v-model:value="partNameQueryParam.name"
                :placeholder="$t('part.search_by_part_name')"
                @search="onSearchName"
                @change="onSearchNameChange"
              >
                <template #enterButton>
                  <a-button :style="getSearchBtn2Style()">
                    <template #icon>
                      <SearchIcon :isActive="!!partNameQueryParam.name" />
                    </template>
                  </a-button>
                </template>
              </a-input-search>
            </a-form-item>
          </a-form>
        </a-col>
      </a-row>

      <span class="j-sub-title sub-title" v-if="vehicles.length > 0">{{
        $t('part.vehicle_list')
      }}</span>

      <a-spin :spinning="loading">
        <div>
          <a-row :gutter="[45, 45]" v-if="vehicles.length > 0">
            <a-col :xs="24" :md="8" v-for="item in vehicles" :key="item.id">
              <PartVehicleItem @checked="handleChecked" :vehicle="item" />
            </a-col>
          </a-row>

          <div class="j-no-result" v-if="loaded && vehicles.length === 0">
            <img
              alt="no-data"
              src="@/assets/images/noData.png"
              width="164"
              height="141"
            />
            <span class="no-data-msg j-content">
              {{ $t('common.coming_soon') }}
            </span>
          </div>
        </div>
      </a-spin>

      <div class="pagination-container" v-if="vehicles.length > 0">
        <a-pagination
          v-model:current="pageNo"
          :page-size-options="pageSizeOptions"
          :total="total"
          show-size-changer
          v-model:pageSize="pageSize"
          @showSizeChange="onShowSizeChange"
          @change="onChange"
          show-quick-jumper
        >
          <template #buildOptionText="props">
            <span>{{ props.value }} {{ $t('common.page_per_item_text') }}</span>
          </template>
        </a-pagination>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import PartVehicleItem from './PartVehicleItem.vue';
import {
  getVehicleList,
  getPartPartsList,
  getVehicleDropdownList
} from '@/api/apis';
import SearchIcon from '@/components/icons/SearchIcon.vue';
import Banner from '@/components/banner/Banner.vue';
import { message } from 'ant-design-vue';
import { useRoute } from 'vue-router';

interface PartProps {
  vehicles: any[];
  vehicleDropdownList: any[];
  [key: string]: any;
}

export default defineComponent({
  name: 'Part',
  data(): PartProps {
    return {
      loading: false,
      loaded: false,
      vehicles: [],
      vehicleDropdownList: [],
      pageNo: 1,
      pageSize: 6,
      pageSizeOptions: ['6', '12'],
      current: 1,
      total: 0,
      partNameQueryParam: {
        name: '',
        vehicleId: undefined
      },
      partNoQueryParam: {
        modelNum: ''
      },
      fetching: false,
      lastFetchId: 0,
      fetchVehicle: null,
      isThroughSelect: false
    };
  },
  components: {
    PartVehicleItem,
    SearchIcon,
    Banner
  },
  created() {
    const route = useRoute();
    localStorage.removeItem('diagramPageNo');
    const params = route.params;

    const query = route.query;

    this.loadData();

    this.fetchVehicle = debounce(value => {
      // 判断value 是不是中文
      if (value.trim() && !/\w[a-zA-Z0-9_]*/.test(value.trim())) {
        message.error(this.$t('part.input_right_vin'));
        return;
      }

      this.lastFetchId += 1;
      const fetchId = this.lastFetchId;
      this.vehicleDropdownList = [];
      this.fetching = true;
      getVehicleDropdownList({
        vin: value.trim()
      }).then(res => {
        if (fetchId !== this.lastFetchId) {
          // for fetch callback order
          return;
        }
        this.vehicleDropdownList = res.result || [];
        this.fetching = false;
      });
    }, 500);
  },
  methods: {
    async loadData() {
      this.loading = true;
      const res = await getVehicleList({
        pageNo: this.pageNo,
        pageSize: this.pageSize
      });

      if (res.success) {
        this.total = res.result.total;

        this.vehicles = res.result.records || [];
      }

      const dRes = await getVehicleDropdownList({
        vin: ''
      });

      if (dRes.success) {
        this.vehicleDropdownList = dRes.result || [];
      }

      this.loading = false;

      this.loaded = true;
    },
    handleChecked(vehicle, isCancel) {
      this.vehicleDropdownList = [];
      if (!isCancel) {
        this.vehicleDropdownList.push(vehicle);
        this.partNameQueryParam = {
          ...this.partNameQueryParam,
          vehicleId: vehicle.id
        };
      } else {
        this.partNameQueryParam = {
          ...this.partNameQueryParam,
          vehicleId: undefined
        };
        if (this.isThroughSelect) {
          this.pageNo = 1;
          this.isThroughSelect = false;
          this.loadData();
        }
      }
    },
    onShowSizeChange() {
      this.pageNo = 1;
      this.loadData();
    },
    onChange() {
      this.loadData();
    },
    getSearchBtn1Style() {
      const isActive = !!this.partNoQueryParam.modelNum;
      return {
        width: 'auto',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    getSearchBtn2Style() {
      const isActive = !!this.partNameQueryParam.name;
      return {
        width: 'auto',
        'background-color': !isActive ? '#E5EAF4' : '#039'
      };
    },
    onSearchNo() {
      const form = (this.$refs as any).modelForm;

      form
        .validate()
        .then(async () => {
          const res = await getPartPartsList({
            modelNum: this.partNoQueryParam.modelNum
              ? this.partNoQueryParam.modelNum.trim()
              : ''
          });
          if (res.success) {
            const data = res.result || {};

            const hasResult = data.total >= 2;
            const vehicleId = data.records[0].vehicleId;
            if (data.total === 1) {
              window.open(
                `/part-detail/${
                  data.records[0].id
                }/${vehicleId}?modelNum=${this.partNoQueryParam.modelNum.trim()}`
              );
            } else {
              window.open(
                `/part-result?isNo=1&modelNum=${this.partNoQueryParam.modelNum.trim()}&hasResult=${Number(
                  hasResult
                )}`
              );
            }
          }
        })
        .catch();
    },
    onSearchName() {
      const form = (this.$refs as any).numForm;
      form
        .validate()
        .then(async () => {
          const res = await getPartPartsList({
            partName: this.partNameQueryParam.name
              ? this.partNameQueryParam.name.trim()
              : '',
            vehicleId: this.partNameQueryParam.vehicleId
          });

          if (res.success) {
            const data = res.result || {};
            const hasResult = data.total >= 2;
            if (data.total === 1) {
              window.open(
                `/partdetail/${data.records[0].id}?name=${this.partNameQueryParam.name}`
              );
            } else {
              const vehicle: any = this.vehicleDropdownList.find(
                (s: any) => s.id === this.partNameQueryParam.vehicleId
              );

              const modelNum = vehicle ? vehicle.modelNum : '';

              window.open(
                `/part-result?isNo=0&partName=${
                  this.partNameQueryParam.name
                }&vehicleId=${
                  this.partNameQueryParam.vehicleId
                }&hasResult=${Number(hasResult)}&model=${modelNum || ''}`
              );
            }
          }
        })
        .catch();
    },
    async handleVehicleChange() {
      const vehicles = cloneDeep(
        this.vehicleDropdownList.filter(
          (s: any) => s.id === this.partNameQueryParam.vehicleId
        )
      );

      this.isThroughSelect = true;

      if (!this.partNameQueryParam.vehicleId) {
        const form = (this.$refs as any).numForm;

        this.$nextTick(() => {
          form.clearValidate('vehicleId');
        });
      }

      if (vehicles.length > 0) {
        // check the item
        this.$bus.$emit('single-check-part-vehicle', vehicles[0].id);

        this.vehicles = vehicles;

        this.total = 1;

        this.pageNo = 1;
      } else {
        this.$bus.$emit('clear-check-part-vehicle');

        this.pageNo = 1;
        await this.loadData();

        // this.vehicleDropdownList = [];
        this.fetchVehicle('');
      }
    },
    onSearchModelNumChange() {
      if (!this.partNoQueryParam.modelNum) {
        const form = (this.$refs as any).modelForm;

        this.$nextTick(() => {
          form.clearValidate('modelNum');
        });
      }
    },
    onSearchNameChange() {
      if (!this.partNameQueryParam.name) {
        const form = (this.$refs as any).numForm;

        this.$nextTick(() => {
          form.clearValidate('name');
        });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.container {
  padding: 0 49px;
  background-color: white;
  .title {
    padding: 32px 0 0 0;
    display: block;
  }

  .sub-title {
    padding: 30px 0;
    display: block;
  }

  .pagination-container {
    padding: 38px 0 40px 0;
    text-align: right;
  }

  .part-name-form,
  .part-no-form {
    display: flex;
    &::v-deep(.ant-form-item) {
      flex: 1;

      & .ant-form-item-control-wrapper {
        width: 100%;
      }

      & .ant-form-item-control {
        line-height: initial;
      }
    }

    &::v-deep(.ant-form-item:last-child) {
      margin-right: 0;
    }
  }
}
::v-deep(.ant-input-group-addon) {
  .ant-btn {
    padding: 0 15px;
  }
}

::v-deep(form:not(.ant-form-vertical) .ant-input-group-wrapper) {
  top: 0;
}
</style>
